import { useCallback, useEffect, useRef, useState } from "react";
import {
  audioParts,
  commentAudioUrl,
  patchComment,
  postEventComment,
  deleteComment,
  thumbUrl,
  type Comment,
} from "./api.js";
import { useAuth } from "./auth.js";
import { MicIcon, PencilIcon, CameraIcon } from "./icons.js";
import { fmtWhen } from "./shared.js";

/**
 * Le fil de commentaires, et tout ce qui touche à un commentaire : qui l'a dit, sa réécoute,
 * et sa correction. Mêmes composants pour un album et pour une photo — c'est le même geste.
 */
function shortAuthor(email: string): string {
  return email.split("@")[0] ?? email;
}

/**
 * Qui a dit ça — le prénom seul (« Maxime Rafalimanana » → « Maxime »). La voix nommée l'emporte sur l'auteur de l'enregistrement : c'est tout
 * l'objet de la diarisation — avant, tout ce qui se disait dans une session était signé de
 * celui qui avait appuyé sur « Enregistrer ». Tant qu'une voix n'est pas nommée, on retombe
 * sur l'auteur plutôt que d'afficher un nom faux.
 */
function firstName(c: Comment): string {
  const nom = (c.speakerName ?? "").trim();
  const full = (nom || c.authorName || shortAuthor(c.author)).trim();
  return full.split(/[\s.]+/)[0] || full;
}

/** Date courte d'un commentaire (« 26 juil. 2026 »). */
function fmtDay(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/** En-tête d'un commentaire : prénom + date (heure précise au survol). */
function CommentWho({ comment }: { comment: Comment }) {
  return (
    <span className="cmt__who">
      {firstName(comment)}
      <time className="cmt__when" dateTime={comment.createdAt} title={fmtWhen(comment.createdAt)}>
        {fmtDay(comment.createdAt)}
      </time>
    </span>
  );
}

/**
 * Réécoute d'un commentaire vocal. Chaque segment est un webm autonome : on les
 * enchaîne à la fin de chacun plutôt que de les concaténer, ce qui ne marcherait pas.
 */
function PlayComment({ comment }: { comment: Comment }) {
  const parts = audioParts(comment);
  const [index, setIndex] = useState<number | null>(null);
  const [t, setT] = useState(0);
  const [duree, setDuree] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  /** Le saut automatique n'a lieu qu'une fois, et jamais après un geste de l'utilisateur. */
  const sauteRef = useRef(false);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setIndex(null);
    setT(0);
    setDuree(0);
  }, []);

  useEffect(() => () => void audioRef.current?.pause(), []);
  if (parts === 0) return null;

  /**
   * Marge avant le premier mot : sauter pile sur l'attaque rognerait la première syllabe.
   */
  const MARGE_MS = 200;
  const onsetDe = (i: number): number => comment.segments?.[i]?.onsetMs ?? 0;
  /**
   * La durée vient du serveur, pas du lecteur : la réponse audio est un flux sans
   * `Content-Length`, donc `audio.duration` reste à 0 dans le navigateur et la barre
   * n'aurait aucune échelle. On accepte quand même celle du lecteur si elle arrive.
   */
  const dureeDe = (i: number): number => (comment.segments?.[i]?.durationMs ?? 0) / 1000;

  const playFrom = (i: number): void => {
    if (i >= parts) {
      stop();
      return;
    }
    const a = new Audio(commentAudioUrl(comment.id, i));
    a.preload = "auto";
    audioRef.current = a;
    sauteRef.current = false;
    setIndex(i);
    setT(0);
    setDuree(dureeDe(i));

    /**
     * Se poser sur le premier mot, dès que le lecteur l'accepte.
     *
     * On réessaie au fil du chargement parce que WebKit ne connaît pas la durée d'un WebM
     * de `MediaRecorder` : mesuré, il annonce 0,48 s pour un fichier de 9,4 s et **clampe**
     * le saut. Sa plage explorable grandit à mesure que les octets arrivent, donc la
     * tentative finit par passer. Chromium et Firefox y arrivent du premier coup.
     */
    const positionner = (): void => {
      // La durée du serveur d'abord : celle du lecteur est fausse en WebKit (0,48 s pour un
      // fichier de 9,4 s). On ne s'en sert qu'à défaut de mesure côté serveur.
      if (!dureeDe(i) && Number.isFinite(a.duration) && a.duration > 0) setDuree(a.duration);
      if (sauteRef.current) return;
      const saut = Math.max(0, onsetDe(i) - MARGE_MS) / 1000;
      if (saut <= 0) {
        sauteRef.current = true;
        return;
      }
      if (a.seekable.length > 0 && saut < a.seekable.end(0)) {
        a.currentTime = saut;
        sauteRef.current = true;
      }
    };
    a.onloadedmetadata = positionner;
    a.ondurationchange = positionner;
    a.oncanplay = positionner;
    a.oncanplaythrough = positionner;
    a.onprogress = positionner;
    a.ontimeupdate = () => setT(a.currentTime);
    a.onended = () => playFrom(i + 1);
    /*
     * Ne pas couper sur `error`. Firefox positionne MEDIA_ERR_DECODE sur ces WebM — leur
     * dernier bloc est tronqué net par `MediaRecorder` — **tout en lisant normalement**
     * (mesuré : canplay atteint, readyState 3, lecture qui avance). L'ancien
     * `onerror = stop` rendait donc la réécoute impossible sous Firefox. On ne s'arrête que
     * si rien n'est réellement lisible.
     */
    a.onerror = () => {
      if (a.readyState < 2) stop();
    };
    void a.play().catch(stop);
  };

  /** Clic n'importe où sur la barre : y compris tout au début, dans le silence. */
  const chercher = (e: React.MouseEvent<HTMLDivElement>): void => {
    const a = audioRef.current;
    const bar = barRef.current;
    if (!a || !bar || !duree) return;
    if (a.seekable.length === 0) return;
    // Dès que tu choisis une position, on ne te la reprend pas.
    sauteRef.current = true;
    const r = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    a.currentTime = ratio * duree;
    setT(a.currentTime);
  };

  const enLecture = index !== null;
  const onset = enLecture ? onsetDe(index) / 1000 : 0;

  return (
    <span className="cplay">
      <button
        className={`cmt__play ${enLecture ? "cmt__play--on" : ""}`}
        onClick={() => (enLecture ? stop() : playFrom(0))}
        title={enLecture ? "Arrêter" : "Réécouter"}
        aria-label={enLecture ? "Arrêter" : "Réécouter"}
        data-testid="cmt-play"
      >
        {enLecture ? "■" : "▶"}
      </button>
      {enLecture && (
        <>
          {/*
            La barre couvre **tout** le segment, silence de tête compris : la lecture démarre
            au premier mot, mais le début reste atteignable d'un clic — l'audio brut ne se
            rogne pas, on choisit seulement où l'écouter.
          */}
          <div
            className="cplay__bar"
            ref={barRef}
            onClick={chercher}
            role="slider"
            tabIndex={0}
            aria-label="Position dans l'enregistrement"
            aria-valuemin={0}
            aria-valuemax={Math.round(duree)}
            aria-valuenow={Math.round(t)}
            data-testid="cmt-bar"
          >
            {duree > 0 && onset > 0 && (
              <span className="cplay__onset" style={{ left: `${(onset / duree) * 100}%` }} />
            )}
            <span
              className="cplay__done"
              style={{ width: duree > 0 ? `${(t / duree) * 100}%` : "0%" }}
            />
          </div>
          <span className="cplay__t">
            {Math.floor(t)}s{parts > 1 ? ` · ${index! + 1}/${parts}` : ""}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * Corps d'un commentaire : qui, quand, réécoute, et texte corrigeable au clic.
 * Corriger sert surtout aux noms propres mal entendus — la transcription d'origine
 * reste conservée côté serveur.
 */
export function CommentBody({
  comment,
  onEdited,
}: {
  comment: Comment;
  onEdited: (c: Comment) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.text);
  const [saving, setSaving] = useState(false);

  const commit = async (): Promise<void> => {
    setEditing(false);
    if (draft.trim() === comment.text.trim() || !draft.trim()) return;
    setSaving(true);
    try {
      onEdited(await patchComment(comment.id, draft));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cmt__body">
      <span className="cmt__head">
        <CommentWho comment={comment} />
        <PlayComment comment={comment} />
        {comment.editedBy && (
          <span className="cmt__edited" title={`Corrigé par ${comment.editedBy}`}>corrigé</span>
        )}
      </span>
      {editing ? (
        <textarea
          className="cmt__input"
          value={draft}
          autoFocus
          rows={2}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void commit();
            }
            if (e.key === "Escape") setEditing(false);
          }}
          data-testid="cmt-edit-input"
        />
      ) : (
        <span
          className="cmt__text editable"
          role="button"
          tabIndex={0}
          title="Cliquer pour corriger"
          onClick={() => {
            setDraft(comment.text);
            setEditing(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setDraft(comment.text);
              setEditing(true);
            }
          }}
          data-testid="cmt-text"
        >
          {comment.text}
        </span>
      )}
    </div>
  );
}

/**
 * Fil de commentaires de l'album — même geste que sur une photo : on lit, on
 * écrit. Les souvenirs rattachés à une photo précise y apparaissent aussi, avec
 * leur vignette (cliquable) pour qu'on sache de quoi ça parle.
 */
export function AlbumComments({
  eventId,
  comments,
  busy,
  onOpenPhoto,
  onChanged,
}: {
  eventId: string;
  comments: Comment[];
  busy: boolean;
  onOpenPhoto: (photoId: string) => void;
  onChanged: () => void;
}) {
  const { state } = useAuth();
  const isOwner = state.status === "authed" && state.isOwner;
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const remove = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteComment(id);
      onChanged();
    } finally {
      setDeletingId(null);
      setConfirmId(null);
    }
  };

  const post = async () => {
    const t = draft.trim();
    if (!t) return;
    setPosting(true);
    try {
      await postEventComment(eventId, t);
      setDraft("");
      onChanged();
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="cbox" data-testid="album-comments">
      <h3 className="cbox__title">
        Commentaires
        {busy && <span className="cbox__busy"> · transcription en cours…</span>}
      </h3>
      {comments.length > 0 && (
        <ul className="cbox__list">
          {comments.map((c) => (
            <li key={c.id} className="cmt" data-testid="album-cmt">
              {c.photoId ? (
                <button
                  className="cbox__thumb"
                  onClick={() => onOpenPhoto(c.photoId!)}
                  title="Voir la photo"
                >
                  <img src={thumbUrl(c.photoId)} alt="" loading="lazy" />
                </button>
              ) : (
                <CommentSourceIcon source={c.source} />
              )}
              <CommentBody comment={c} onEdited={onChanged} />
              {isOwner &&
                (confirmId === c.id ? (
                  <span className="cbox__confirm">
                    Supprimer ?
                    <button
                      className="cbox__yes"
                      disabled={deletingId === c.id}
                      onClick={() => void remove(c.id)}
                    >
                      Oui
                    </button>
                    <button className="cbox__no" onClick={() => setConfirmId(null)}>
                      Non
                    </button>
                  </span>
                ) : (
                  <button
                    className="cmt__del"
                    title="Supprimer ce commentaire"
                    data-testid="album-cmt-del"
                    onClick={() => setConfirmId(c.id)}
                  >
                    ×
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}
      <div className="cadd">
        <input
          className="cadd__input"
          value={draft}
          placeholder="Commenter cet album…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void post();
          }}
          data-testid="album-cmt-input"
        />
        <button
          className="cadd__btn"
          disabled={posting || !draft.trim()}
          onClick={() => void post()}
          data-testid="album-cmt-post"
        >
          Envoyer
        </button>
      </div>
    </section>
  );
}

export function CommentSourceIcon({ source }: { source: string }) {
  if (source === "voice") return <MicIcon className="cmt__ico" />;
  if (source === "flickr") return <CameraIcon className="cmt__ico" />;
  return <PencilIcon className="cmt__ico" />;
}
